import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { InAppBrowser } from '@capgo/inappbrowser';
import { API_URL, TENANT } from '../../config/constants';

// On native (Capacitor) the webview origin is capacitor://localhost / https://localhost,
// so the API cookie is third-party: the session travels as a Bearer token instead.
// API calls go through CapacitorHttp (capacitor.config.json), so no CORS setup is needed on the API.
export const IS_NATIVE = Capacitor.isNativePlatform();

const TOKEN_KEY = 'access_token';

// kept in memory so axios can read it synchronously; loaded once at boot
let accessToken = null;

export const loadAccessToken = async () => {
    if (!IS_NATIVE) return;
    accessToken = (await Preferences.get({ key: TOKEN_KEY })).value;
};

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
    accessToken = token;
    return Preferences.set({ key: TOKEN_KEY, value: token });
};

// the backend still accepts the cookie as a fallback: a leftover one (SAML webview, login
// responses stored by the native http stack) would keep the session alive after logout
const clearSessionCookie = (origin = new URL(API_URL).origin) => CapacitorCookies.deleteCookie({
    // an origin, not API_URL: Android derives the cookie path from the url and the cookie lives on "/"
    url: origin, key: TOKEN_KEY,
}).catch(() => {});

export const clearAccessToken = () => {
    accessToken = null;
    clearSessionCookie();
    return Preferences.remove({ key: TOKEN_KEY });
};

// SAML runs the plain web flow in an in-app webview: the callback sets the session cookie on the
// API host and redirects to /{tenant}/exams (or /{tenant}/error_page?msg=). The session JWT is read
// from the native cookie store, so no backend change is needed for the app.
export const nativeSamlLogin = (title) => new Promise(async (resolve) => {
    let listeners = [];
    let finished = false;
    const finish = (result) => {
        if (finished) return;
        finished = true;
        listeners.forEach(l => l.remove());
        InAppBrowser.close().catch(() => {});
        resolve(result);
    };
    listeners = await Promise.all([
        InAppBrowser.addListener('urlChangeEvent', async ({ url }) => {
            const { origin, pathname, searchParams } = new URL(url);
            if (pathname.startsWith(`/${TENANT}/error_page`)) return finish({ error: searchParams.get('msg') || 'Internal_Error' });
            if (!pathname.startsWith(`/${TENANT}/exams`)) return;
            // the callback sets the cookie on server_name, the host it redirects to (may differ from API_URL)
            const { access_token } = await InAppBrowser.getCookies({ url: origin, includeHttpOnly: true });
            if (access_token) await setAccessToken(access_token);
            clearSessionCookie(origin);
            finish({ error: access_token ? null : 'Internal_Error' });
        }),
        InAppBrowser.addListener('closeEvent', () => finish({ cancelled: true })),
    ]);
    await InAppBrowser.openWebView({ url: `${API_URL}/start_saml_login`, title });
});
